import React from "react";
import api from "@services/api";
import Loader from "@components/Loader";
import "@styles/opencart-admin.css";

// WN-0214 — Sales by User: per-user order counts + sales by order type.
// Defaults to the logged-in user and today; a dropdown switches users and the
// date range is editable. Data comes from /reports/sales-by-user.

const todayStr = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const money = (v) =>
  Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// WN-0232 — order source options (maps to backend SOURCE_MAP keys).
const SOURCES = [
  { key: "all", label: "All" },
  { key: "backoffice", label: "Back-office" },
  { key: "ios", label: "App (iOS)" },
  { key: "android", label: "App (Android)" },
  { key: "app", label: "App (all)" },
  { key: "website", label: "Website" },
];

export default function SalesByUserPage() {
  const [users, setUsers] = React.useState([]);
  const [user, setUser] = React.useState("");
  const [from, setFrom] = React.useState(todayStr());
  const [to, setTo] = React.useState(todayStr());
  const [source, setSource] = React.useState("all");

  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [usersLoading, setUsersLoading] = React.useState(true);

  // WN-0232 — "All users overview" (ignores the user dropdown).
  const [overview, setOverview] = React.useState(null);
  const [ovLoading, setOvLoading] = React.useState(false);
  // Expand a person in View B to show their order-type breakdown.
  const [expanded, setExpanded] = React.useState(null); // the person's name
  const [breakdown, setBreakdown] = React.useState({}); // name -> rows | "loading"

  const toggleExpand = React.useCallback(
    (name) => {
      if (expanded === name) {
        setExpanded(null);
        return;
      }
      setExpanded(name);
      if (!breakdown[name]) {
        setBreakdown((b) => ({ ...b, [name]: "loading" }));
        api
          .get("/reports/sales-by-user", { params: { user: name, from, to, source } })
          .then((r) => setBreakdown((b) => ({ ...b, [name]: r.data?.rows || [] })))
          .catch(() => setBreakdown((b) => ({ ...b, [name]: [] })));
      }
    },
    [expanded, breakdown, from, to, source]
  );

  // Clear cached breakdowns when the range/source changes.
  React.useEffect(() => {
    setExpanded(null);
    setBreakdown({});
  }, [from, to, source]);

  // Load the user list + default (logged-in user) once.
  React.useEffect(() => {
    api
      .get("/reports/sales-by-user/users")
      .then((r) => {
        const list = r.data?.users || [];
        setUsers(list);
        const def = r.data?.default || (list[0] && list[0].created_by) || "";
        setUser(def);
      })
      .catch(() => setErr("Failed to load the user list."))
      .finally(() => setUsersLoading(false));
  }, []);

  // (Re)load the report whenever the user or date range changes.
  React.useEffect(() => {
    if (!user) return;
    setLoading(true);
    setErr("");
    api
      .get("/reports/sales-by-user", { params: { user, from, to, source } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.message || "Failed to load the report."))
      .finally(() => setLoading(false));
  }, [user, from, to, source]);

  // (Re)load the all-users overview whenever date range or source changes.
  React.useEffect(() => {
    setOvLoading(true);
    api
      .get("/reports/sales-by-user/overview", { params: { from, to, source } })
      .then((r) => setOverview(r.data))
      .catch(() => setOverview(null))
      .finally(() => setOvLoading(false));
  }, [from, to, source]);

  const rows = data?.rows || [];

  return (
    <div className="oc-content">
      <div className="oc-header">
        <h1>Sales by User</h1>
        <nav className="oc-bc">
          <span>Reports</span>
          <span className="sep">›</span>
          <span>Sales by User</span>
        </nav>
      </div>

      {/* Filters */}
      <div className="oc-card" style={{ marginBottom: 16 }}>
        <div className="oc-toolbar" style={{ gap: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div className="oc-field">
            <label>User</label>
            <select
              className="oc-input"
              value={user}
              onChange={(e) => setUser(e.target.value)}
              disabled={usersLoading}
              style={{ minWidth: 220 }}
            >
              {usersLoading && <option>Loading…</option>}
              {users.map((u) => (
                <option key={u.created_by} value={u.created_by}>
                  {u.created_by} ({u.orders})
                </option>
              ))}
            </select>
          </div>
          <div className="oc-field">
            <label>From</label>
            <input type="date" className="oc-input" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="oc-field">
            <label>To</label>
            <input type="date" className="oc-input" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div className="oc-field">
            <label>Source</label>
            <select
              className="oc-input"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              style={{ minWidth: 150 }}
            >
              {SOURCES.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div className="oc-field">
            <label>&nbsp;</label>
            <button
              className="oc-btn ghost"
              onClick={() => {
                setFrom(todayStr());
                setTo(todayStr());
              }}
            >
              Today
            </button>
          </div>
        </div>
      </div>

      {err && (
        <div className="oc-card" style={{ color: "#c00", marginBottom: 12 }}>
          {err}
        </div>
      )}

      {/* Results */}
      <div className="oc-card">
        {loading ? (
          <Loader />
        ) : rows.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: "#777" }}>
            No orders for {data?.user || "this user"} between {data?.from || from} and {data?.to || to}.
          </div>
        ) : (
          <table className="oc-table">
            <thead>
              <tr>
                <th>Order Type</th>
                <th style={{ textAlign: "right" }}>Orders</th>
                <th style={{ textAlign: "right" }}>Sales (JOD)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.order_type}>
                  <td style={{ textTransform: "capitalize" }}>{r.order_type}</td>
                  <td style={{ textAlign: "right" }}>{r.orders}</td>
                  <td style={{ textAlign: "right" }}>{money(r.sales)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 700, borderTop: "2px solid #ddd" }}>
                <td>Total</td>
                <td style={{ textAlign: "right" }}>{data?.totals?.orders ?? 0}</td>
                <td style={{ textAlign: "right" }}>{money(data?.totals?.sales)}</td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {/* WN-0232 — All users overview (ignores the User dropdown above). */}
      <div className="oc-header" style={{ marginTop: 28 }}>
        <h1 style={{ fontSize: 20 }}>All users overview</h1>
      </div>

      {ovLoading ? (
        <div className="oc-card"><Loader /></div>
      ) : !overview ? (
        <div className="oc-card" style={{ color: "#777", padding: 20 }}>No overview data.</div>
      ) : (
        <>
          {/* View A — summary cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: 12,
              marginBottom: 16,
            }}
          >
            {[
              { label: "Total", c: overview.totals, big: true },
              { label: "Back-office", c: overview.by_source?.backoffice },
              { label: "App (iOS)", c: overview.by_source?.ios },
              { label: "App (Android)", c: overview.by_source?.android },
              { label: "Website", c: overview.by_source?.website },
            ].map((card) => (
              <div
                key={card.label}
                className="oc-card"
                style={{
                  padding: 14,
                  textAlign: "center",
                  border: card.big ? "2px solid #2563eb" : undefined,
                }}
              >
                <div style={{ fontSize: 12, color: "#666", textTransform: "uppercase", letterSpacing: 0.4 }}>
                  {card.label}
                </div>
                <div style={{ fontSize: 24, fontWeight: 800, margin: "4px 0" }}>{card.c?.orders ?? 0}</div>
                <div style={{ fontSize: 12, color: "#888" }}>orders</div>
                <div style={{ fontSize: 14, fontWeight: 600, marginTop: 6 }}>{money(card.c?.sales)} JOD</div>
              </div>
            ))}
          </div>

          {/* View B — ranked "who made how many orders" */}
          <div className="oc-card">
            <div style={{ fontWeight: 700, padding: "6px 4px 12px" }}>
              Orders per person {overview.source && overview.source !== "all" ? `(${overview.source})` : ""}
            </div>
            {(overview.ranked || []).length === 0 ? (
              <div style={{ padding: 16, color: "#777", textAlign: "center" }}>No orders in this range.</div>
            ) : (
              <table className="oc-table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th>Name</th>
                    <th style={{ width: "40%" }}>Orders</th>
                    <th style={{ textAlign: "right" }}>Sales (JOD)</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const max = Math.max(...overview.ranked.map((r) => r.orders), 1);
                    return overview.ranked.flatMap((r, i) => {
                      const canExpand = true;
                      const isOpen = expanded === r.name;
                      const bd = breakdown[r.name];
                      const main = (
                        <tr
                          key={r.name}
                          onClick={canExpand ? () => toggleExpand(r.name) : undefined}
                          style={{
                            ...(r.is_customer ? { background: "#fff7ed" } : {}),
                            cursor: canExpand ? "pointer" : "default",
                          }}
                        >
                          <td style={{ color: "#999" }}>{r.is_customer ? "" : i + 1}</td>
                          <td style={r.is_customer ? { fontWeight: 600, color: "#b45309" } : undefined}>
                            {canExpand && <span style={{ color: "#999", marginRight: 6 }}>{isOpen ? "▾" : "▸"}</span>}
                            {r.name}
                          </td>
                          <td>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div
                                style={{
                                  height: 14,
                                  width: `${Math.max(2, (r.orders / max) * 100)}%`,
                                  background: r.is_customer ? "#f59e0b" : "#2563eb",
                                  borderRadius: 3,
                                  minWidth: 2,
                                }}
                              />
                              <span style={{ fontWeight: 600 }}>{r.orders}</span>
                            </div>
                          </td>
                          <td style={{ textAlign: "right" }}>{money(r.sales)}</td>
                        </tr>
                      );
                      if (!isOpen) return [main];
                      const sub = (
                        <tr key={r.name + "__bd"}>
                          <td></td>
                          <td colSpan={3} style={{ background: "#f8fafc", padding: "8px 12px" }}>
                            {bd === "loading" || !bd ? (
                              <span style={{ color: "#888" }}>Loading breakdown…</span>
                            ) : bd.length === 0 ? (
                              <span style={{ color: "#888" }}>No order-type breakdown.</span>
                            ) : (
                              <table style={{ width: "100%", fontSize: 13 }}>
                                <tbody>
                                  {bd.map((t) => (
                                    <tr key={t.order_type}>
                                      <td style={{ textTransform: "capitalize", padding: "2px 0" }}>{t.order_type}</td>
                                      <td style={{ textAlign: "right", width: 80 }}>{t.orders}</td>
                                      <td style={{ textAlign: "right", width: 120 }}>{money(t.sales)} JOD</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      );
                      return [main, sub];
                    });
                  })()}
                </tbody>
              </table>
            )}
          </div>

          {/* View C (Person by source matrix) removed per Omar's request (WN-0232). */}

          {/* View D — matrix: order type by source */}
          {(overview.by_order_type || []).length > 0 && (
            <div className="oc-card" style={{ marginTop: 16 }}>
              <div style={{ fontWeight: 700, padding: "6px 4px 12px" }}>Order type by source</div>
              <div style={{ overflowX: "auto" }}>
                <table className="oc-table" style={{ minWidth: 640 }}>
                  <thead>
                    <tr>
                      <th style={{ position: "sticky", left: 0, background: "#fff", zIndex: 1 }}>Order Type</th>
                      <th style={{ textAlign: "right" }}>Back-office</th>
                      <th style={{ textAlign: "right" }}>iOS</th>
                      <th style={{ textAlign: "right" }}>Android</th>
                      <th style={{ textAlign: "right" }}>Website</th>
                      <th style={{ textAlign: "right" }}>Total</th>
                      <th style={{ textAlign: "right" }}>Sales (JOD)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(overview.by_order_type || []).map((t) => (
                      <tr key={t.order_type}>
                        <td
                          style={{
                            position: "sticky",
                            left: 0,
                            background: "#fff",
                            textTransform: "capitalize",
                          }}
                        >
                          {t.order_type}
                        </td>
                        <td style={{ textAlign: "right" }}>{t.backoffice}</td>
                        <td style={{ textAlign: "right" }}>{t.ios}</td>
                        <td style={{ textAlign: "right" }}>{t.android}</td>
                        <td style={{ textAlign: "right" }}>{t.website}</td>
                        <td style={{ textAlign: "right", fontWeight: 700 }}>{t.orders}</td>
                        <td style={{ textAlign: "right" }}>{money(t.sales)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ fontWeight: 700, borderTop: "2px solid #ddd" }}>
                      <td style={{ position: "sticky", left: 0, background: "#fafafa" }}>Total</td>
                      <td style={{ textAlign: "right" }}>{overview.by_source?.backoffice?.orders ?? 0}</td>
                      <td style={{ textAlign: "right" }}>{overview.by_source?.ios?.orders ?? 0}</td>
                      <td style={{ textAlign: "right" }}>{overview.by_source?.android?.orders ?? 0}</td>
                      <td style={{ textAlign: "right" }}>{overview.by_source?.website?.orders ?? 0}</td>
                      <td style={{ textAlign: "right" }}>{overview.totals?.orders ?? 0}</td>
                      <td style={{ textAlign: "right" }}>{money(overview.totals?.sales)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
